import React, { useState, useEffect } from 'react';
import { useTranslation } from '../context/TranslationContext';
import { API_ENDPOINTS } from '../utils/config';
import { useAuth } from '../context/AuthContext';
import './UserAdminRequestDetailModal.css';

interface UserAdminRequestDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  requestId: string | null;
  onApprove: (requestId: string) => void;
  onReject: (requestId: string, reason: string) => void;
}

interface RequestDetail {
  _id: string;
  product_name: string;
  product_brand: string;
  product_global_code: string;
  product_company_code: string;
  company_product_code: string;
  company_product_code_label: string;
  product_description: string;
  product_category: string;
  quantity: number;
  unit: string;
  budget: number;
  currency: string;
  delivery_time: string;
  importance: string;
  address: string;
  urgency: string;
  current_price: number;
  current_supplier: string;
  favorite_suppliers: string[];
  additional_specifications: string[];
  custom_specifications: any[];
  additional_notes: string;
  region: string[];
  category: string;
  dimensions_text: string;
  base_unit: {
    unit: string;
    conversion_factor: number;
  };
  is_ocr_generated: boolean;
  ocr_data: any;
  ocr_prices: any;
  ocr_supplier_info: any;
  status: string;
  phase: string;
  company_approval_status: string;
  created_at: string;
  updated_at: string;
  user_name: string;
  user_email: string;
  user_department: string;
  company_id: string;
  company_name: string;
}

const UserAdminRequestDetailModal: React.FC<UserAdminRequestDetailModalProps> = ({
  isOpen,
  onClose,
  requestId,
  onApprove,
  onReject
}) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [requestDetail, setRequestDetail] = useState<RequestDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  useEffect(() => {
    if (isOpen && requestId) {
      fetchRequestDetail();
    }
  }, [isOpen, requestId]);

  const fetchRequestDetail = async () => {
    if (!requestId || !token) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.REQUEST_DETAIL(requestId)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setRequestDetail(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching request detail:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = () => {
    if (requestId) {
      onApprove(requestId);
      onClose();
    }
  };

  const handleReject = () => {
    if (requestId && rejectionReason.trim()) {
      onReject(requestId, rejectionReason.trim());
      setShowRejectModal(false);
      setRejectionReason('');
      onClose();
    }
  };

  const handleClose = () => {
    setRequestDetail(null);
    setRejectionReason('');
    setShowRejectModal(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="useradmin-request-detail-modal">
        <div className="modal-header">
          <h2>{t('common.request_details')}</h2>
          <button className="close-button" onClick={handleClose}>×</button>
        </div>

        <div className="modal-content">
          {isLoading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : requestDetail ? (
            <div className="request-details">
              {/* Basic Information */}
              <div className="detail-section">
                <h3>{t('common.basic_information')}</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>{t('common.product_name')}:</label>
                    <span>{requestDetail.product_name}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.product_brand')}:</label>
                    <span>{requestDetail.product_brand || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{requestDetail.company_product_code_label || 'Company Product Code'}:</label>
                    <span>{requestDetail.company_product_code || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.product_description')}:</label>
                    <span>{requestDetail.product_description || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.quantity')}:</label>
                    <span>{requestDetail.quantity} {requestDetail.unit}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.budget')}:</label>
                    <span>{requestDetail.currency} {requestDetail.budget}</span>
                  </div>
                </div>
              </div>

              {/* Product Specifications */}
              {(requestDetail.dimensions_text || requestDetail.base_unit) && (
                <div className="detail-section">
                  <h3>{t('common.product_specifications')}</h3>
                  <div className="detail-grid">
                    {requestDetail.dimensions_text && (
                      <div className="detail-item">
                        <label>{t('common.dimensions')}:</label>
                        <span>{requestDetail.dimensions_text}</span>
                      </div>
                    )}
                    {requestDetail.base_unit && (
                      <div className="detail-item">
                        <label>{t('common.base_unit')}:</label>
                        <span>{requestDetail.base_unit.unit} ({t('common.factor')}: {requestDetail.base_unit.conversion_factor})</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Request Information */}
              <div className="detail-section">
                <h3>{t('common.request_information')}</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>{t('common.delivery_time')}:</label>
                    <span>{requestDetail.delivery_time || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.importance')}:</label>
                    <span>{requestDetail.importance || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.urgency')}:</label>
                    <span>{requestDetail.urgency || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.region')}:</label>
                    <span>{requestDetail.region?.join(', ') || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.category')}:</label>
                    <span>{requestDetail.category || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.address')}:</label>
                    <span>{requestDetail.address || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Supplier Information */}
              <div className="detail-section">
                <h3>{t('common.supplier_information')}</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>{t('common.current_supplier')}:</label>
                    <span>{requestDetail.current_supplier || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.current_price')}:</label>
                    <span>{requestDetail.current_price ? `${requestDetail.currency} ${requestDetail.current_price}` : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.favorite_suppliers')}:</label>
                    <span>{requestDetail.favorite_suppliers?.length ? requestDetail.favorite_suppliers.join(', ') : 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="detail-section">
                <h3>{t('common.additional_information')}</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>{t('common.additional_specifications')}:</label>
                    <span>{requestDetail.additional_specifications?.length ? requestDetail.additional_specifications.join(', ') : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.additional_notes')}:</label>
                    <span>{requestDetail.additional_notes || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.ocr_generated')}:</label>
                    <span>{requestDetail.is_ocr_generated ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>

              {/* User Information */}
              <div className="detail-section">
                <h3>{t('common.user_information')}</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>{t('common.user_name')}:</label>
                    <span>{requestDetail.user_name}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.user_email')}:</label>
                    <span>{requestDetail.user_email}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.user_department')}:</label>
                    <span>{requestDetail.user_department || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* OCR Data (if available) */}
              {requestDetail.is_ocr_generated && requestDetail.ocr_data && (
                <div className="detail-section">
                  <h3>{t('common.ocr_data')}</h3>
                  <div className="ocr-data">
                    <pre>{JSON.stringify(requestDetail.ocr_data, null, 2)}</pre>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="error">{t('common.failed_to_load_data')}</div>
          )}
        </div>

        <div className="modal-footer">
          <div className="action-buttons">
            <button 
              className="btn btn-approve" 
              onClick={handleApprove}
              disabled={!requestDetail}
            >
              {t('common.approve')}
            </button>
            <button 
              className="btn btn-reject" 
              onClick={() => setShowRejectModal(true)}
              disabled={!requestDetail}
            >
              {t('common.reject')}
            </button>
            <button className="btn btn-secondary" onClick={handleClose}>
              {t('common.close')}
            </button>
          </div>
        </div>

        {/* Rejection Modal */}
        {showRejectModal && (
          <div className="modal-overlay">
            <div className="rejection-modal">
              <div className="modal-header">
                <h3>{t('common.reject_request')}</h3>
                <button className="close-button" onClick={() => setShowRejectModal(false)}>×</button>
              </div>
              <div className="modal-content">
                <div className="form-group">
                  <label>{t('common.rejection_reason')}:</label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder={t('common.enter_rejection_reason')}
                    rows={4}
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  className="btn btn-reject" 
                  onClick={handleReject}
                  disabled={!rejectionReason.trim()}
                >
                  {t('common.reject')}
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowRejectModal(false)}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserAdminRequestDetailModal;
